import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { PageHeader } from "@/components/common/PageHeader";
import { QueryState } from "@/components/common/AsyncState";
import { fetchBranches } from "@/api/branches";
import { fetchScheduleGrid } from "@/api/groups";
import { DAYSET_LABELS } from "@/types/enums";
import type { Dayset } from "@/types/enums";
import { useAuth } from "@/auth/useAuth";
import { GroupFormDialog } from "@/pages/groups/GroupFormDialog";

export function SchedulePage() {
  const navigate = useNavigate();
  const { user, hasPermission } = useAuth();
  const canEdit = hasPermission("schedule.edit");
  const { data: branches } = useQuery({ queryKey: ["branches"], queryFn: fetchBranches });
  const [branchId, setBranchId] = useState(user?.branchId ?? "");
  const [dayset, setDayset] = useState<Dayset>("MON_WED_FRI");
  const [prefill, setPrefill] = useState<{ branchId?: string; roomId?: string; timeSlot?: string; dayset?: Dayset } | null>(null);

  const effectiveBranchId = branchId || branches?.[0]?.id || "";

  const { data: grid, isLoading, error } = useQuery({
    queryKey: ["schedule-grid", effectiveBranchId, dayset],
    queryFn: () => fetchScheduleGrid({ branchId: effectiveBranchId, dayset }),
    enabled: Boolean(effectiveBranchId),
  });

  return (
    <>
      <PageHeader
        title="Расписание"
        subtitle="Матрица «кабинет × время» — какая группа занимает каждую ячейку"
        actions={
          canEdit && (
            <Button variant="contained" onClick={() => setPrefill({ branchId: effectiveBranchId, dayset })}>
              Новая группа
            </Button>
          )
        }
      />

      <Paper className="crm-page-filter" variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
          <TextField select label="Филиал" size="small" value={effectiveBranchId} onChange={(e) => setBranchId(e.target.value)} sx={{ minWidth: 200 }}>
            {(branches ?? []).map((b) => (
              <MenuItem key={b.id} value={b.id}>
                {b.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField select label="Дни" size="small" value={dayset} onChange={(e) => setDayset(e.target.value as Dayset)} sx={{ minWidth: 160 }}>
            {Object.entries(DAYSET_LABELS).map(([key, label]) => (
              <MenuItem key={key} value={key}>
                {label}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      </Paper>

      <Paper className="crm-schedule-grid" variant="outlined" sx={{ overflowX: "auto" }}>
        <QueryState isLoading={isLoading} error={error}>
          {grid && (
            <Table size="small" sx={{ minWidth: 720 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Кабинет</TableCell>
                  {grid.timeSlots.map((slot) => (
                    <TableCell key={slot} align="center">
                      {slot}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {grid.rooms.map((room) => (
                  <TableRow key={room.id}>
                    <TableCell component="th" sx={{ whiteSpace: "nowrap" }}>
                      {room.name}
                      <Typography variant="caption" color="text.secondary" display="block">
                        вместимость {room.capacity}
                      </Typography>
                    </TableCell>
                    {grid.timeSlots.map((slot) => {
                      const cell = grid.cells.find((c) => c.roomId === room.id && c.timeSlot === slot);
                      const group = cell?.group;
                      return (
                        <TableCell
                          key={slot}
                          align="center"
                          className={group ? "crm-schedule-grid__busy" : "crm-schedule-grid__free"}
                          sx={{ cursor: group || canEdit ? "pointer" : "default", minWidth: 130 }}
                          onClick={() =>
                            group
                              ? navigate(`/groups/${group.id}`)
                              : canEdit && setPrefill({ branchId: effectiveBranchId, roomId: room.id, timeSlot: slot, dayset })
                          }
                        >
                          {group ? (
                            <Box>
                              <Typography variant="body2" fontWeight={600}>
                                {group.code} · {group.subjectName ?? group.subjectId}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" display="block">
                                {group.teacherName ?? "—"}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" display="block">
                                {group.level} · {group.studentsCount ?? 0} чел.
                              </Typography>
                            </Box>
                          ) : (
                            <Typography variant="caption" color="text.disabled">
                              {canEdit ? "+" : "—"}
                            </Typography>
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </QueryState>
      </Paper>

      {canEdit && <GroupFormDialog open={Boolean(prefill)} onClose={() => setPrefill(null)} prefill={prefill ?? undefined} />}
    </>
  );
}
